import { useEffect, useState } from "react";
import { ApiError, api, formatBirth, type CalcReport, type CalculatorInfo, type CalcRunBody, type UserView } from "../api";
import { DateField, isFullDate, LoadError, Overlay, Skeleton, Spinner } from "../ui";
import { ReportView } from "./ReportView";

const CATEGORIES: { id: CalculatorInfo["category"]; title: string; hint: string }[] = [
  { id: "numerology", title: "Нумерология", hint: "Считаем по цифрам даты рождения" },
  { id: "eastern", title: "Восточные", hint: "Китайские и корейские четыре столпа" },
  { id: "astro", title: "Астрология", hint: "Считаем по положению Луны и Солнца" },
];

const ERROR_TEXT: Record<string, string> = {
  bad_date: "Проверьте дату: такого дня нет или он в будущем.",
  bad_date2: "Проверьте вторую дату: такого дня нет или он в будущем.",
  bad_time: "Проверьте время рождения.",
  need_date2: "Нужна вторая дата.",
  network: "Нет связи. Попробуйте ещё раз.",
  unauthorized: "Откройте приложение из Telegram.",
};

/** Смещение часового пояса на момент рождения по часам этого устройства (учитывает переход на летнее время). */
function offsetAt(date: string, time?: string): number {
  const [d, m, y] = date.split(".").map(Number) as [number, number, number];
  const [h, mi] = time ? (time.split(":").map(Number) as [number, number]) : [12, 0];
  return -new Date(y, m - 1, d, h, mi).getTimezoneOffset();
}

interface Props {
  user: UserView;
  /** Открыть конкретный калькулятор сразу, без похода по каталогу (например, по карточке с главной). */
  autoOpenId?: string | null;
  onAutoOpened?: () => void;
}

/** Вкладка «Калькуляторы»: каталог, форма ввода и отчёт. Все расчёты и тексты приходят с сервера. */
export function Calculators({ user, autoOpenId, onAutoOpened }: Props) {
  const [list, setList] = useState<CalculatorInfo[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<CalculatorInfo | null>(null);

  const load = () => {
    setFailed(false);
    api.calculators().then(
      (r) => setList(r.calculators),
      () => setFailed(true),
    );
  };
  useEffect(load, []);

  useEffect(() => {
    if (!autoOpenId || !list) return;
    const found = list.find((x) => x.id === autoOpenId);
    if (found) setOpen(found);
    onAutoOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenId, list]);

  return (
    <div className="warm">
      <div className="pagehead">
        <h1>Калькуляторы</h1>
        <p>Расчёты по дате рождения: на себя, близких и пары</p>
      </div>
      <div className="sheet" style={{ minHeight: "60vh" }}>
        {!list && !failed && <Skeleton rows={4} tall />}
        {failed && !list && <LoadError text="Не удалось загрузить список." onRetry={load} />}
        {list &&
          CATEGORIES.map((c) => {
            const items = list.filter((x) => x.category === c.id);
            if (!items.length) return null;
            return (
              <section key={c.id} className="calcsec">
                <h2>{c.title}</h2>
                <p className="hint">{c.hint}</p>
                {items.map((x) => (
                  <button key={x.id} className="calcitem" onClick={() => setOpen(x)}>
                    <b>{x.title}</b>
                    <span>{x.blurb}</span>
                  </button>
                ))}
              </section>
            );
          })}
      </div>
      {open && <CalcOverlay info={open} user={user} onClose={() => setOpen(null)} />}
    </div>
  );
}

function CalcOverlay({ info, user, onClose }: { info: CalculatorInfo; user: UserView; onClose: () => void }) {
  const own = formatBirth(user.birthDate);
  const [date, setDate] = useState(own);
  const [date2, setDate2] = useState("");
  const [noTime, setNoTime] = useState(true);
  const [hour, setHour] = useState("12");
  const [minute, setMinute] = useState("00");
  const [gender, setGender] = useState<"m" | "f" | undefined>(undefined);
  const [surname, setSurname] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<CalcReport | null>(null);

  const needDate = info.input.date !== undefined;
  const needDate2 = !!info.input.date2;
  const needSurname = !!info.input.surname;
  const needGender = info.input.gender === "required";
  const ready =
    (!needDate || isFullDate(date)) &&
    (!needDate2 || isFullDate(date2)) &&
    (!needSurname || surname.trim().length > 0) &&
    (!needGender || !!gender) &&
    !busy;

  const run = async () => {
    setBusy(true);
    setError(null);
    const time = needDate && info.input.time && !noTime ? `${hour}:${minute}` : undefined;
    const body: CalcRunBody = {
      ...(needDate ? { date } : {}),
      ...(needDate2 ? { date2 } : {}),
      ...(time ? { time } : {}),
      ...(info.input.gender && gender ? { gender } : {}),
      ...(needSurname ? { surname: surname.trim() } : {}),
      utcOffsetMinutes: offsetAt(date, time),
    };
    try {
      setReport(await api.runCalculator(info.id, body));
    } catch (e) {
      setError(ERROR_TEXT[e instanceof ApiError ? e.code : "network"] ?? "Не удалось рассчитать. Попробуйте ещё раз.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay title={info.title} onClose={report ? () => setReport(null) : onClose} back>
      {report ? (
        <div className="sheet">
          <ReportView report={report} />
        </div>
      ) : (
        <div className="sheet">
          <p className="rp-lead">{info.blurb}</p>
          {needSurname && (
            <div className="field">
              <span>Фамилия</span>
              <input
                type="text"
                className="text"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                placeholder="Фамилия, данная при рождении"
              />
            </div>
          )}
          {needDate && <DateField label={needDate2 ? "Дата рождения первого" : "Дата рождения"} value={date} onChange={setDate} />}
          {needDate2 && <DateField label="Дата рождения второго" value={date2} onChange={setDate2} />}
          {info.input.time && (
            <div className="field">
              <span>Время рождения</span>
              <label className="check">
                <input type="checkbox" checked={noTime} onChange={(e) => setNoTime(e.target.checked)} />
                <i>Не знаю время</i>
              </label>
              {!noTime && (
                <div className="timerow">
                  <select className="text" value={hour} onChange={(e) => setHour(e.target.value)} aria-label="Часы">
                    {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => (
                      <option key={h}>{h}</option>
                    ))}
                  </select>
                  <b>:</b>
                  <select className="text" value={minute} onChange={(e) => setMinute(e.target.value)} aria-label="Минуты">
                    {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")).map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
          {info.input.gender && (
            <div className="field">
              <span>{needGender ? "Пол" : "Пол (нужен для периодов удачи)"}</span>
              <div className="chiprow" style={{ padding: 0 }}>
                {(["f", "m"] as const).map((g) => (
                  <button key={g} className={`pill${gender === g ? " on" : ""}`} onClick={() => setGender(gender === g ? undefined : g)}>
                    {g === "f" ? "Женский" : "Мужской"}
                  </button>
                ))}
              </div>
            </div>
          )}
          <p className="hint">🔒 Даты используются только для расчёта и не сохраняются.</p>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="formspace" />
          <button className="primary" disabled={!ready} onClick={() => void run()}>
            {busy ? <Spinner /> : "Рассчитать"}
          </button>
        </div>
      )}
    </Overlay>
  );
}
